import './instrument';
// Styles
import 'nprogress/nprogress.css';
import 'react-virtualized/styles.css';
import './global.css';

// Project Imports
import { PostHogProvider } from '@posthog/react';
import { ErrorBoundary } from '@sentry/react';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { HelmetProvider } from 'react-helmet-async';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { PersistGate } from 'redux-persist/integration/react';
// Project Imports
import App from 'src/App';
import { TenantProvider } from 'src/contexts/SelectedTenantContext';
import { SidebarProvider } from 'src/contexts/SidebarContext';
import { ThemeProvider } from 'src/contexts/TailwindThemeProvider';
import ScrollTop from 'src/hooks/useScrollTop';
import { persistor, store } from 'src/redux/store';

import { ToastProvider } from './components/ui/toast';
import { ANALYTICS_KEY } from './config';
import {
  getKCRealmName,
  getKcToken,
  getRealmName,
  isValidToken,
  kcinit
} from './keycloak';
import { kc, updateToken } from './keycloak';
import { getExternalAnalyticsConfig } from './utils/analyticsUtils';
import { PUBLIC_ROUTES } from './utils/constants';
import logger from './utils/logger';
import { discoverTenantAndInitialize, isPublicRoute } from './utils/utils';
import { saveLangFromUrl } from './utils/utils';
export function renderApp(divId: string) {
  const container = document.getElementById(divId);

  const root = ReactDOM.createRoot(container);

  root.render(
    <React.StrictMode>
      <PostHogProvider
        apiKey={ANALYTICS_KEY}
        options={getExternalAnalyticsConfig()}
      >
        <HelmetProvider>
          <Provider store={store}>
            <PersistGate loading={null} persistor={persistor}>
              <ThemeProvider defaultTheme="light" storageKey="egpt-ui-theme">
                <ToastProvider>
                  <SidebarProvider>
                    <TenantProvider>
                      <BrowserRouter>
                        <ScrollTop />
                        <ErrorBoundary>
                          <App />
                        </ErrorBoundary>
                      </BrowserRouter>
                    </TenantProvider>
                  </SidebarProvider>
                </ToastProvider>
              </ThemeProvider>
            </PersistGate>
          </Provider>
        </HelmetProvider>
      </PostHogProvider>
    </React.StrictMode>,
    document.getElementById(divId)
  );
}

function validateAndRefreshToken() {
  const kcToken = getKcToken();
  if (!kcToken || !isValidToken(kcToken)) {
    if (!PUBLIC_ROUTES.includes(window.location.pathname)) {
      void updateToken(kc).then(() => {
        if (!isValidToken(getKcToken())) {
          window.location.reload();
        }
      });
    }
  }
}

function init() {
  const divId = 'root-component';

  let isFocusLost = false;
  document.addEventListener('visibilitychange', function () {
    const kcToken = getKcToken();

    if (document.hidden) {
      //tab blurred
      if (kcToken) isFocusLost = true;
    } else {
      //Page is in user view
      if (isFocusLost) {
        isFocusLost = false;
        // If tab was switched, re-check token (another tab may have refreshed)
        validateAndRefreshToken();
      }
    }
  });

  window.onstorage = (e: any) => {
    if (e.key === 'prm') {
      logger.log(e.key, e.newValue);
    }
  };

  window.onoffline = (e: any) => {
    isFocusLost = true;
  };

  window.ononline = (e: any) => {
    if (isFocusLost) {
      isFocusLost = false;
      validateAndRefreshToken();
    }
  };

  try {
    saveLangFromUrl();
    if (isPublicRoute()) {
      renderApp(divId);
    } else {
      const hasRealm = getRealmName() || getKCRealmName();
      if (!hasRealm) {
        discoverTenantAndInitialize(divId);
      } else {
        kcinit().then(() => {
          renderApp(divId);
        });
      }
    }
  } catch (error: any) {
    logger.error(error.message || error);
  }
}

init();
